import { addThousandSeparator } from '@openeducation/oe-js-utils';
import { Language } from '@openeducation/oe-rn-shared-types';
import {
  styled,
  secondaryFontFamily,
  ThemeType,
} from '@openeducation/oe-rn-themes';
import React, { memo, FC } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';

import { OEText } from '../../atoms/oe-text/oe-text';
import { Skeleton } from '../../atoms/skeleton/skeleton';

export interface StatsCardProps {
  num?: number | string;
  description: string;
  language?: Language;
  fontSize?: number;
  isSmall?: boolean;
  style?: StyleProp<ViewStyle>;
  isLoading?: boolean;
  color?: string;
}

const StatsCard: FC<StatsCardProps> = ({
  num = 0,
  description,
  isSmall,
  style,
  fontSize,
  isLoading,
  color,
  language = Language.EN,
}) => {
  const renderContent = () => {
    if (isLoading) {
      return <Skeleton height={isSmall ? 78 : 124} />;
    }
    return (
      <>
        <NumText
          isSmall={isSmall}
          color={color}
          fontSize={fontSize || isSmall ? 24 : 40}
        >
          {addThousandSeparator(num, language)}
        </NumText>
        <Description>{description}</Description>
      </>
    );
  };

  return (
    <Container style={style} isSmall={isSmall} isLoading={isLoading}>
      {renderContent()}
    </Container>
  );
};

type ContainerType = {
  isSmall?: boolean;
  isLoading?: boolean;
};

const Container = styled.View<ContainerType>`
  background-color: ${({ theme }) => theme.color.lightPrimary};
  padding: ${({ isSmall, isLoading }) => (isLoading ? 0 : isSmall ? 16 : 20)}px;
  border-radius: 7px;
  shadow-radius: 1px;
  shadow-opacity: 0.1;
  shadow-color: ${({ theme }) => theme.color.black};
  shadow-offset: 0 1px;
  flex-direction: ${({ isSmall }) => (isSmall ? 'row' : 'column')};
  align-items: ${({ isSmall }) => (isSmall ? 'center' : 'flex-start')};
  justify-content: ${({ isSmall }) => (isSmall ? 'flex-start' : 'center')};
  flex: 1;
`;

type NumTextType = {
  theme: ThemeType;
  isSmall?: boolean;
  color?: string;
};

const NumText = styled(OEText).attrs(({ theme, color }: NumTextType) => ({
  color: color || theme.color.primary,
  fontType: 'bold',
  fontFamily: secondaryFontFamily,
}))<NumTextType>`
  padding-right: ${({ isSmall }) => (isSmall ? 4 : 0)}px;
`;

const Description = styled(OEText).attrs(({ theme }) => ({
  color: theme.color.text,
  fontSize: 16,
  fontFamily: secondaryFontFamily,
}))``;

export default memo(StatsCard);
